import { Button, Col, Divider, Drawer, EditButton, Icons, Row, Show, Space, Tag, Tooltip, Typography } from "@pankod/refine-antd";
import { IResourceComponentsProps, useCan, useOne, useShow } from "@pankod/refine-core";
import { ManageBackgroundScreening } from "components/backgroundscreening/manageBackgroundScreening";
import { RenderTimelineModal, StateButtons } from "components/common";
import { ShowUserCard } from "components/profile/showUserCard";
import { LookupButton } from "components/utils/lookupButton";
import { ObjectMetadata } from "components/utils/objectMetadata";
import dayjs from "dayjs";
import { IBackgroundScreening, IBaseProps, IScreeningConfigSet, IScreeningMetadata } from "interfaces";
import { useEffect, useState } from "react";
import { buildAttachmentUrl, DATAPROVIDER_CONFIGSET, DATAPROVIDER_LOOKUP, DATAPROVIDER_READ, DATAPROVIDER_UPDATE, doesExist, RESOURCE_PATH, showDash } from "scripts/site";

const { Title, Text } = Typography;

export const BackgroundScreeningShow: React.FC<IResourceComponentsProps> = () => {
  const { queryResult } = useShow<IBackgroundScreening>({
    dataProviderName: DATAPROVIDER_READ,
  });
  const { data, isFetching } = queryResult;
  const record = data?.data as IBackgroundScreening;

  const { data: metadata } = useOne<IScreeningMetadata>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.METADATA,
    id: RESOURCE_PATH.BACKGROUND_SCREENING
  });

  const { data: configSet } = useOne<IScreeningConfigSet>({
    dataProviderName: DATAPROVIDER_CONFIGSET,
    resource: RESOURCE_PATH.BACKGROUND_SCREENING,
    id: record?.id ?? "",
    queryOptions: {
      enabled: !!record?.id
    }
  });

  const metaConfig = metadata?.data?.config;
  // Set the rules for the request based on the config set. If config set is not found, then use the metadata config as default.
  const configSetRules = configSet?.data?.values ?? metaConfig?.defaultConfigSet;

  const canEdit = useCan({
    resource: RESOURCE_PATH.BACKGROUND_SCREENING,
    action: "edit",
    params: {
      dataProviderName: DATAPROVIDER_UPDATE,
      id: record?.id
    },
    queryOptions: {
      enabled: !!record?.id
    }
  });

  const twoColumnLayout = {
    xs: { span: 24 },
    sm: { span: 24 },
    md: { span: 16 },
    lg: { span: 10 },
    xl: { span: 10 },
    xxl: { span: 16 },
  };

  const rowGutter = { xs: 8, sm: 16, md: 24, lg: 32 };

  const [refetchData, switchRefetchFlag] = useState(false);

  const refetchBackgroundScreeningData = () => {
    switchRefetchFlag(true);
  }

  useEffect(() => {
    if (refetchData) {
      queryResult.refetch();
      switchRefetchFlag(false);
    }
  }, [refetchData, queryResult]);

  const currentStateMetadata = metadata?.data?.states?.find((state) => state.state === record?.stateManager?.state);

  // Manage button and Drawer logic
  const [reviewFormOpen, switchReviewForm] = useState(false);
  const onFormSubmit = () => {
    switchReviewForm(false);
  };
  const manageActions = metadata?.data?.states?.find(st =>
    st.state === record?.stateManager?.state
  )?.nextStates?.filter((st) => (configSetRules?.stateButtonsHideActions ?? []).includes(st?.action));

  const showTrainingDocument = record?.hasTcsEmployeeId !== false;

  return (
    <Show
      isLoading={isFetching}
      headerProps={{
        title: record?.name,
        backIcon: true,
        tags: <Tag color={currentStateMetadata?.color ?? "default"}>
          {currentStateMetadata?.displayName ?? record?.stateManager?.state ?? "Loading"}
        </Tag>,
        extra: (!isFetching) && record &&
          <Space>
            {canEdit.data?.can && <>
              {configSetRules?.editableStates?.includes(record?.stateManager?.state) &&
                <EditButton></EditButton>
              }
              {(manageActions?.length ?? 0) > 0 && configSetRules?.canManageRequest &&
                <Button
                  type="primary"
                  key="manage"
                  onClick={() => { switchReviewForm(true) }}
                  icon={<Icons.FormOutlined />}>
                  {manageActions?.length === 1 ? manageActions?.at(0)?.buttonText : "Manage"}
                </Button>
              }
              <StateButtons {...record as IBaseProps}
                resourceToAct={RESOURCE_PATH.BACKGROUND_SCREENING}
                resourceToActId={record?.id || "0"}
                hideActions={configSetRules?.stateButtonsHideActions}
                customPostStateAction={() => {
                  refetchBackgroundScreeningData();
                }}
              />
            </>}

            {record && <RenderTimelineModal {...(record as IBaseProps)} />}
          </Space>
      }}
    >
      <Drawer
        title="Manage Request"
        placement="right"
        size="default"
        open={reviewFormOpen}
        onClose={() => { switchReviewForm(false) }}
      >
        {metadata?.data && record &&
          <ManageBackgroundScreening
            backgroundScreeningRecord={record ?? null}
            screeningMetadata={metadata?.data}
            onFormSubmission={onFormSubmit}
            refetch={refetchBackgroundScreeningData}
            actions={manageActions}
          />
        }
      </Drawer>

      {/* State and Assigned To - mirrored from cloud screening */}
      <Row gutter={rowGutter} className="mt-15">
        <Col className="gutter-row" {...twoColumnLayout}>
          <Title level={5}>State</Title>
          <Tag color={currentStateMetadata?.color ?? "default"}>
            {currentStateMetadata?.displayName ?? record?.stateManager?.state ?? "---"}
          </Tag>
        </Col>
        <Col>
          <Title level={5}>Assigned To</Title>
          <Space wrap split={<Divider type="vertical" />} >
            {(record?.stateManager?.assignedTo?.length ?? 0) > 0
              ? record?.stateManager?.assignedTo?.map((e, i) => (
                <ShowUserCard key={`other-${i}`} id={e} />
              ))
              : "None"}
          </Space>
        </Col>
      </Row>

      {record?.screeningId &&
        <Row gutter={rowGutter} className="mt-15">
          <Col className="gutter-row" {...twoColumnLayout}>
            <Title level={5}>Screening Id</Title>
            <Text copyable={doesExist(record?.screeningId)}>{showDash(record?.screeningId)}</Text>
          </Col>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Title level={5}>Screening Date</Title>
            <Text copyable={doesExist(record?.screeningDate)}>
              {record?.screeningDate ? dayjs(record.screeningDate).format("LL") : "---"}
            </Text>
          </Col>
        </Row>
      }

      <Divider orientation="left"></Divider>

      <Row gutter={rowGutter} className="mt-15">
        <Col className="gutter-row" {...twoColumnLayout}>
          <Title level={5}>Associate First Name</Title>
          <Text copyable={doesExist(record?.firstName)}>{showDash(record?.firstName)}</Text>
        </Col>
        <Col className="gutter-row" {...twoColumnLayout}>
          <Title level={5}>Associate Last Name</Title>
          <Text copyable={doesExist(record?.lastName)}>{showDash(record?.lastName)}</Text>
        </Col>
      </Row>

      <Row gutter={rowGutter} className="mt-15">
        <Col className="gutter-row" {...twoColumnLayout}>
          <Title level={5}>TCS Employee Id</Title>
          <Text copyable={doesExist(record?.tcsEmployeeId)}>{showDash(record?.tcsEmployeeId)}</Text>
        </Col>
        <Col className="gutter-row" {...twoColumnLayout}>
          <Title level={5}>{record?.hasTcsEmployeeId === false ? "Personal Email" : "TCS Email Id"}</Title>
          <Text copyable={record?.hasTcsEmployeeId === false ? doesExist(record?.personalEmailId) : doesExist(record?.tcsEmailId)}>
            {record?.hasTcsEmployeeId === false
              ? showDash(record?.personalEmailId)
              : showDash(record?.tcsEmailId)}
          </Text>
        </Col>
      </Row>

      <Row gutter={rowGutter} className="mt-15">
        <Col className="gutter-row" {...twoColumnLayout}>
          <Title level={5}>Country</Title>
          <LookupButton
            id={record?.countryId}
            resource={RESOURCE_PATH.COUNTRY}
            noClick
          />
        </Col>
        <Col className="gutter-row" {...twoColumnLayout}>
          <Title level={5}>Location</Title>
          <LookupButton
            id={record?.locationId}
            resource={RESOURCE_PATH.LOCATION}
            noClick
          />
        </Col>
      </Row>
      <Row gutter={rowGutter} className="mt-15">
        <Col className="gutter-row" {...twoColumnLayout}>
          <Title level={5}>TCS Manager</Title>
          <ShowUserCard id={record?.managerUserId} />
        </Col>
        <Col className="gutter-row" {...twoColumnLayout}>
          <Title level={5}>WON</Title>
          <Text copyable={doesExist(record?.won)}>{showDash(record?.won)}</Text>
        </Col>
      </Row>

      <Row gutter={rowGutter} className="mt-15">
        <Col className="gutter-row" {...twoColumnLayout}>
          <Title level={5}>Microsoft Alias (v-)</Title>
          <Text copyable={doesExist(record?.microsoftAlias)}>{showDash(record?.microsoftAlias)}</Text>
        </Col>
        <Col className="gutter-row" {...twoColumnLayout}>
          <Title level={5}>MS Personnel Number</Title>
          <Text copyable={record?.msPersonnelNumber !== undefined && record?.msPersonnelNumber !== null}>{record?.msPersonnelNumber ?? "---"}</Text>
        </Col>
      </Row>

      <Title level={5} style={{marginTop: 15}}>Remarks</Title>
      <Text style={{ whiteSpace: 'pre-wrap' }}>{showDash(record?.remarks)}</Text>

      <Divider orientation="left">Documents</Divider>
      <Row gutter={rowGutter} className="mt-15">
        <Col className="gutter-row" {...twoColumnLayout}>
          <Title level={5}>Consent Document</Title>
          <Text>
            {record?.consentDocument?.length > 0 ? (() => {
              const consentDoc = record.consentDocument[0];
              const originalName = consentDoc?.name ?? "Document";
              return (
                <Tooltip title={originalName} placement="topLeft">
                  <Button
                    icon={<Icons.FileOutlined />}
                    className="doc-btn"
                    onClick={() => window.open(
                      buildAttachmentUrl(
                        RESOURCE_PATH.BACKGROUND_SCREENING,
                        record?.id,
                        consentDoc?.response?.url,
                        { download: true, filename: consentDoc?.name }
                      ),
                      "_blank"
                    )}
                  >
                    <span className="doc-btn-text">{originalName}</span>
                  </Button>
                </Tooltip>
              );
            })() : "---"}
          </Text>
        </Col>
        {showTrainingDocument && (
          <Col className="gutter-row" {...twoColumnLayout}>
            <Title level={5}>Completed Training Document</Title>
            <Text>
              {record?.completedTrainingDocument?.length > 0 ? (() => {
                const trainingDoc = record.completedTrainingDocument[0];
                const originalName = trainingDoc?.name ?? "Document";
                return (
                  <Tooltip title={originalName} placement="topLeft">
                    <Button
                      icon={<Icons.FileOutlined />}
                      className="doc-btn"
                      onClick={() => window.open(
                        buildAttachmentUrl(
                          RESOURCE_PATH.BACKGROUND_SCREENING,
                          record?.id,
                          trainingDoc?.response?.url,
                          { download: true, filename: trainingDoc?.name }
                        ),
                        "_blank"
                      )}
                    >
                      <span className="doc-btn-text">{originalName}</span>
                    </Button>
                  </Tooltip>
                );
              })() : "---"}
            </Text>
          </Col>
        )}
      </Row>

      <Divider></Divider>
      <div className="mt-15">
        <ObjectMetadata {...record as IBaseProps} ></ObjectMetadata>
      </div>
    </Show>
  );
};